import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBuilding,
  faCalendarCheck,
  faClipboardList,
  faCommentDots,
  faStar,
  faTag,
  faPaperclip,
  faFingerprint,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { ZoomIn, ZoomOut, RotateCcw, RefreshCw, X } from "lucide-react";
import { showErrorToast } from "../../../../../components/ConfirmDialog";
import { getTaskById } from "../../../../../api/hrm/tasks/task_info";

const TaskPopupCompleted = ({ isOpen, onClose, taskId }) => {
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);

  useEffect(() => {
    if (!taskId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getTaskById(taskId);
        setTask(res);
      } catch (err) {
        showErrorToast("Không thể tải chi tiết công việc");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [taskId]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/45 backdrop-blur-sm p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      {/* Main Modal Panel */}
      <div
        className="relative flex h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-300 bg-white shadow-2xl animate-in slide-in-from-bottom duration-300 sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* --- HEADER --- */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-gradient-to-r from-red-50/70 to-white px-5 py-4 sm:px-6">
          <h2 className="truncate text-sm font-black uppercase tracking-widest text-gray-800 sm:text-base">
            Chi tiết <span className="text-red-700">hoàn thành</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="group flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600 active:scale-95"
          >
            <X className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* --- BODY --- */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-5 antialiased scrollbar-thin scrollbar-thumb-gray-200 sm:p-8">
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
              <p className="text-gray-400 font-black uppercase text-[10px] tracking-widest">Truy xuất dữ liệu...</p>
            </div>
          ) : !task ? (
            <div className="py-10 text-center text-red-500 font-bold uppercase text-xs">Dữ liệu không tồn tại</div>
          ) : (
            <>
              {/* 📋 SECTION 1: HEADER INFO CARD */}
              <div className="bg-white border border-gray-300 rounded-[2rem] p-5 shadow-sm grid grid-cols-2 gap-4">
                <HeaderItem icon={faUser} label="Nhân viên" value={task.employee_name} color="text-blue-500" />
                <HeaderItem icon={faCalendarCheck} label="Ngày làm" value={dayjs(task.work_day).format("DD/MM/YYYY")} color="text-green-500" />
                <HeaderItem icon={faBuilding} label="Chi nhánh" value={task.branch_name} color="text-purple-500" />
                <HeaderItem icon={faTag} label="Loại" value={task.task_type === "assigned" ? "Giao việc" : "Đăng ký"} color="text-orange-500" />
              </div>

              {/* 📝 SECTION 2: TASK CONTENT */}
              <div className="space-y-2">
                <Label text="Nội dung công việc" icon={faClipboardList} />
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-300 group transition-all hover:bg-white hover:shadow-md">
                  <p className="text-gray-800 font-bold text-base leading-relaxed italic">"{task.taskname}"</p>
                  {task.note && <p className="mt-3 text-gray-500 text-sm border-l-2 border-gray-200 pl-3">{task.note}</p>}
                  {task.attachment_url && (
                    <button 
                      onClick={() => window.open(task.attachment_url, "_blank")}
                      className="mt-4 flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-black uppercase text-gray-600 hover:text-red-600 hover:border-red-200 transition-all shadow-sm"
                    >
                      <FontAwesomeIcon icon={faPaperclip} /> File giao việc
                    </button>
                  )}
                </div>
              </div>

              {/* 💬 SECTION 3: REPORT */}
              <div className="space-y-2">
                <Label text="Báo cáo thực hiện" icon={faCommentDots} />
                {task.reports?.length > 0 ? (
                  <div className="bg-blue-50/50 rounded-2xl p-4 border border-blue-100">
                    <p className="text-blue-900 font-bold leading-relaxed">{task.reports[0].report_content}</p>
                    {task.reports[0].files?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {task.reports[0].files.map((f, i) => (
                          <button
                            key={i}
                            onClick={() => setPreviewImage(f.file_url)}
                            className="px-3 py-1.5 bg-white border border-blue-200 rounded-lg text-[10px] font-black uppercase text-blue-600 shadow-sm hover:scale-105 transition-transform"
                          >
                            🖼️ View Image
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="mt-4 text-[10px] font-black text-blue-400 uppercase tracking-tighter">
                      Gửi lúc: {dayjs(task.reports[0].reported_at).format("HH:mm - DD/MM/YYYY")}
                    </div>
                  </div>
                ) : (
                  <EmptyState text="Chưa có báo cáo từ nhân viên" />
                )}
              </div>

              {/* ⭐ SECTION 4: JUDGMENT (HIGHLIGHT) */}
              <div className="space-y-2">
                <Label text="Đánh giá & Kết quả" icon={faStar} />
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] p-6 text-white overflow-hidden shadow-xl">
                  <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="space-y-1">
                      <p className="text-red-400 font-black uppercase text-[10px] tracking-[0.2em]">Nhận xét quản lý</p>
                      <p className="text-lg font-medium italic">
                        {task.judge_content || "Công việc đã hoàn thành."}
                      </p>
                      <div className="pt-2 flex items-center gap-2 text-gray-400 text-[10px] font-bold uppercase tracking-tighter">
                        <FontAwesomeIcon icon={faFingerprint} />
                        {task.judged_by_name || "Hệ thống"} • {dayjs(task.judged_at).format("DD/MM/YYYY")}
                      </div>
                    </div>

                    {task.judge_point != null && (
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20 text-center min-w-[80px]">
                        <div className="text-[10px] font-black text-gray-400 uppercase mb-1">Điểm</div>
                        <div className={`text-3xl font-black ${task.judge_point >= 5 ? 'text-green-400' : 'text-yellow-400'}`}>
                          {task.judge_point}
                        </div>
                      </div>
                    )}
                  </div>
                  <FontAwesomeIcon icon={faCircleCheck} className="absolute -bottom-4 -right-4 text-white/5 text-8xl rotate-12" />
                </div>
              </div>

              {/* 🆔 FOOTER */}
              <div className="pt-4 flex justify-center gap-6 text-[9px] font-black text-gray-300 uppercase tracking-[0.3em]">
                <span>ID: {task.id}</span>
                <span>Status: {task.status}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modern Image Preview Overlay */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center backdrop-blur-md">
          <div className="absolute top-6 right-6 flex gap-3">
             <ImageAction icon={<RefreshCw size={20}/>} onClick={() => {setZoom(1); setRotate(0);}} />
             <ImageAction icon={<X size={20}/>} onClick={() => setPreviewImage(null)} color="bg-red-500" />
          </div>
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-6 bg-white/10 backdrop-blur-xl px-8 py-3 rounded-full border border-white/20">
            <button onClick={() => setZoom(z => Math.max(0.5, z - 0.2))} className="text-white hover:text-red-400"><ZoomOut/></button>
            <span className="text-white font-black w-12 text-center text-xs">{(zoom * 100).toFixed(0)}%</span>
            <button onClick={() => setZoom(z => z + 0.2)} className="text-white hover:text-red-400"><ZoomIn/></button>
            <div className="w-px h-4 bg-white/20 mx-2"></div>
            <button onClick={() => setRotate(r => r + 90)} className="text-white hover:text-red-400"><RotateCcw/></button>
          </div>
          <img
            src={previewImage}
            style={{ transform: `scale(${zoom}) rotate(${rotate}deg)`, transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
            className="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

// Sub-components
const Label = ({ text, icon }) => (
  <div className="flex items-center gap-2 text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">
    <FontAwesomeIcon icon={icon} className="text-[10px]" />
    {text}
  </div>
);

const HeaderItem = ({ icon, label, value, color }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">{label}</span>
    <div className="flex items-center gap-2">
      <FontAwesomeIcon icon={icon} className={`${color} text-xs`} />
      <span className="text-xs font-bold text-gray-700 truncate">{value || "--"}</span>
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="py-4 px-6 border-2 border-dashed border-gray-300 rounded-2xl text-center text-gray-300 text-xs font-bold italic uppercase tracking-wider">
    {text}
  </div>
);

const ImageAction = ({ icon, onClick, color = "bg-white/10" }) => (
  <button onClick={onClick} className={`${color} p-3 rounded-full text-white hover:scale-110 active:scale-95 transition-all backdrop-blur-md`}>
    {icon}
  </button>
);

export default TaskPopupCompleted;